import { PASSWORD_MIN, passwordProblems } from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { AuthLayout, FormError } from '../components/auth/auth-layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

// The page an emailed set-password link opens (D393): sent when access is approved, asked for from the sign-in page
// ("Forgot your password?") or sent by an admin. It checks the link first, so it can say who it is for or that it
// has expired; saving the password spends the link and signs the person out everywhere.

export const Route = createFileRoute('/set-password')({
  validateSearch: (s: Record<string, unknown>) => ({ token: typeof s.token === 'string' ? s.token : '' }),
  component: SetPassword,
});

const toLogin = (
  <Link to="/login" search={{ next: undefined }} className="text-sm font-medium underline">
    Go to sign in
  </Link>
);

function SetPassword() {
  const { token } = Route.useSearch();
  const link = useQuery({
    queryKey: ['password-link', token],
    queryFn: () => api<{ email: string; purpose: 'invite' | 'reset' }>('/auth/password/link', { body: { token } }),
    enabled: !!token,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (done)
    return (
      <AuthLayout title="Password saved">
        <p className="text-sm text-muted-foreground" role="status">
          Your password is set. Sign in with your email address and the new password.
        </p>
        {toLogin}
      </AuthLayout>
    );
  if (!token || link.isError)
    return (
      <AuthLayout title="This link doesn't work">
        <p className="text-sm text-muted-foreground">
          {link.error instanceof ApiError ? link.error.message : 'The link is incomplete.'} On the sign-in page, choose{' '}
          <em>Forgot your password?</em> to get a new one.
        </p>
        {toLogin}
      </AuthLayout>
    );
  if (!link.data) return <AuthLayout title="Set your password">{null}</AuthLayout>;

  const problems = password ? passwordProblems(password, link.data.email) : [];
  const mismatch = again.length > 0 && again !== password;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api('/auth/password/set', { body: { token, password } });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title={link.data.purpose === 'invite' ? 'Set your password' : 'Choose a new password'}>
      <form className="space-y-4" onSubmit={submit}>
        <p className="text-sm text-muted-foreground">
          For <strong className="text-foreground">{link.data.email}</strong>. Use at least {PASSWORD_MIN} characters; a few unrelated words
          make a strong password that is easy to type.
        </p>
        {/* Lets password managers file the new password under the right account. */}
        <input type="email" name="username" autoComplete="username" value={link.data.email} readOnly hidden />
        <div className="space-y-1.5">
          <label htmlFor="new-password" className="text-sm font-medium">
            New password
          </label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN}
            value={password}
            onChange={e => setPassword(e.target.value)}
            aria-describedby="password-problems"
          />
          <ul id="password-problems" className="text-xs text-muted-foreground" aria-live="polite">
            {problems.map(p => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="again-password" className="text-sm font-medium">
            Type it again
          </label>
          <Input
            id="again-password"
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onChange={e => setAgain(e.target.value)}
            aria-invalid={mismatch || undefined}
          />
          {mismatch && <p className="text-xs text-danger-foreground">The two passwords are different.</p>}
        </div>
        <FormError message={error} />
        <Button type="submit" className="w-full" disabled={busy || !password || problems.length > 0 || again !== password}>
          Save password
        </Button>
      </form>
    </AuthLayout>
  );
}
